import { ReportDomain } from '@mathflat/domain/@entities/(Content)/Report'
import { format } from 'date-fns'
import { action, makeObservable, observable } from 'mobx'
import { computedFn } from 'mobx-utils'
import { EMPTY_NUMBER_ID, EMPTY_STRING_ID } from 'src/constant/common'
import type { DateStr } from 'src/types'

import dateUtils from '@/utils/DateUtils'

import { DTO } from '../DTO'

export type ReportData = Omit<
  res.Report,
  'startDate' | 'endDate' | 'worksheetTagFlags' | 'selfLearningFlag'
> & {
  startDate: DateStr<'-'>
  endDate: DateStr<'-'>
  learningHistory: {
    worksheetTagFlags: res.Report['worksheetTagFlags']
    selfLearningFlag: res.Report['selfLearningFlag']
    studentWorksheetIds?: number[]
    studentWorkbookIds?: number[]
  }
}

export class Report extends DTO implements ReportData {
  static OLD_COMPONENT_LIST = [
    { label: '주별 분석', value: ReportDomain.OLD_COMPONENT_SECTIONS['주별분석'] },
    { label: '단원별 분석', value: ReportDomain.OLD_COMPONENT_SECTIONS['단원별분석'] },
    { label: '난이도별 분석', value: ReportDomain.OLD_COMPONENT_SECTIONS['난이도별분석'] },
    { label: '단원별 세부 페이지', value: ReportDomain.OLD_COMPONENT_SECTIONS['단원별세부페이지'] },
    { label: '학습 내역', value: ReportDomain.OLD_COMPONENT_SECTIONS['학습내역'] },
    {
      label: '영역별 분석 (내용 영역·행동 영역)',
      value: ReportDomain.OLD_COMPONENT_SECTIONS['영역별분석'],
    },
  ] as const

  id = EMPTY_NUMBER_ID
  studentId = EMPTY_STRING_ID
  comment = EMPTY_STRING_ID
  pdfUrl = EMPTY_STRING_ID
  chapter = EMPTY_STRING_ID
  areaScore = EMPTY_NUMBER_ID
  behaviorScore = EMPTY_NUMBER_ID
  totalScore = EMPTY_NUMBER_ID
  totalTier = EMPTY_NUMBER_ID
  createDate = EMPTY_STRING_ID
  type: res.Report['type'] = 'MONTHLY'
  yearMonth = dateUtils.toYearMonth(new Date(), '-')
  startDate = dateUtils.toDateString(new Date(), '-')
  endDate = dateUtils.toDateString(new Date(), '-')
  title = EMPTY_STRING_ID
  extraScore = EMPTY_NUMBER_ID
  learningHistory: ReportData['learningHistory'] = {
    worksheetTagFlags: [],
    selfLearningFlag: false,
    studentWorksheetIds: [],
    studentWorkbookIds: [],
  }
  revision: common.Revision = 'CURRICULUM_15'

  componentFlags: ReportData['componentFlags'] = []
  opened = false
  deleted = false
  sending = false

  constructor(data: Partial<res.Report>) {
    super()

    makeObservable(this, {
      id: observable,
      studentId: observable,
      comment: observable,
      pdfUrl: observable,
      chapter: observable,
      areaScore: observable,
      behaviorScore: observable,
      totalScore: observable,
      totalTier: observable,
      createDate: observable,
      type: observable,
      yearMonth: observable,
      startDate: observable,
      endDate: observable,
      title: observable,
      extraScore: observable,
      learningHistory: observable,
      componentFlags: observable,
      opened: observable,
      sending: observable,
      deleted: observable,
      revision: observable,
      reportRangeDate: action,
    })

    this.update<ReportData>({
      ...data,
      learningHistory: {
        worksheetTagFlags: data.worksheetTagFlags ?? [],
        selfLearningFlag: data.selfLearningFlag ?? false,
        studentWorksheetIds: [],
        studentWorkbookIds: [],
      },
    })
  }

  reportRangeDate = computedFn((yearOpt: 'yy' | 'yyyy') => {
    if (this.type === 'CUSTOM_PERIOD') {
      const startDate = format(new Date(this.startDate), `${yearOpt}.MM.dd`)
      const endDate = format(new Date(this.endDate), `${yearOpt}.MM.dd`)
      return `${startDate}~${endDate}`
    }
    return format(new Date(this.yearMonth), `${yearOpt}년 MM월`)
  })
}
